import { ElementRef, Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, first, mergeMap, share, ReplaySubject, map, distinctUntilChanged, last, take } from 'rxjs';
import { DynamicFacade } from '../store/dynamic.facade';
import { AuthFacade } from 'src/app/auth/store/auth.facade';
import { UiComponent } from '../models/ui-component.models';
import { LabelComponent } from '../controls/label/label.component';
import { RowComponent } from '../controls/row/row.component';
import { ColumnComponent } from '../controls/column/column.component';
import { LabelWithTitleComponent } from '../controls/label-with-title/label-with-title.component';
import { TabComponent } from '../controls/tab/tab.component';
import { LinkComponent } from '../controls/link/link.component';
import { InputComponent } from '../controls/input/input.component';
import { UiAction } from '../models/ui-action.models';
import { DropDownComponent } from '../controls/drop-down/drop-down.component';
import { ExecuteSpRequest } from '../models/execute-sp-request.models';
import { SpRequestBodyInputParameter } from '../models/sp-request-body-input-parameter.models';
import { TableComponent } from '../controls/table/table.component';
import { ButtonComponent } from '../controls/button/button.component';
import { ConvertStoreDataToElements } from 'src/app/shared/helpers/store-data-helper';
import { SpInputParameter2 } from '../models/sp-input-parameter2.models';
import { ExecuteSpResponse } from 'src/app/dynamic-ui/models/execute-sp-response.models';
import { CardComponent } from 'src/app/dynamic-ui/controls/card/card.component';
import { GridComponent } from 'src/app/dynamic-ui/controls/grid/grid.component';
import { GridItemComponent } from 'src/app/dynamic-ui/controls/grid-item/grid-item.component';
import { TitleComponent } from 'src/app/dynamic-ui/controls/title/title.component';
import { ExpansionPanelComponent } from '../controls/expansion-panel/expansion-panel.component';
import { AccordionComponent } from '../controls/accordion/accordion.component';
import { isArray, isEqual } from 'lodash-es';
import { DividerComponent } from '../controls/divider/divider.component';
import { Router, UrlSerializer } from '@angular/router';
import { TreeComponent } from '../controls/tree/tree.component';
import { GeneratePDFRequest } from '../models/generate-pdf-request.models';
import { GeneratePDFResponse } from '../models/generate-pdf-response.models';
import { SendMailRequest } from '../models/send-mail-request.models';
import { SendMailResponse } from '../models/send-mail-response.models';
import { PdfViewerComponent } from '../controls/pdf-viewer/pdf-viewer.component';
import { CheckboxComponent } from '../controls/checkbox/checkbox.component';
import { FileSelectorComponent } from '../controls/file-selector/file-selector.component';
import { FormComponent } from 'src/app/dynamic-ui/controls/form/form.component';
import { SubmitButtonComponent } from '../controls/submit-button/submit-button.component';
import { AppConstants } from 'src/app/app/helpers/app-constants';
import { GetUiDefinitionRequest } from '../models/get-ui-definition-request.models';
import { GetUiDefinitionResponse } from '../models/get-ui-definition-response.models';
import { ImageComponent } from '../controls/image/image.component';
import { TargetComponent } from '../controls/target/target.component';
import { UiDefinitionComponent } from '../controls/ui-definition/ui-definition.component';
import { ExportCsvResponse } from '../models/export-csv-response.models';
import { ExportCsvRequest } from '../models/export-csv-request.models';
import { Presentation } from '../models/presentation.models';

@Injectable({
    providedIn: 'root',
})
export class DynamicUiService {
    private serviceApiUrl: string;
    private formControls: { [key: string]: ElementRef[] } = {};

    uiDefinitionCache: { [key: string]: Observable<GetUiDefinitionResponse> } = {};

    constructor(
        private readonly http: HttpClient,
        @Inject('BASE_URL') baseUrl: string,
        private dynamicFacade: DynamicFacade,
        private authFacade: AuthFacade,
        private router: Router,
        private urlSerializer: UrlSerializer
    ) {
        this.serviceApiUrl = `${baseUrl}api/dynamic`;
    }

    InitService() {
        this.formControls = {};
        //TODO: Implementieren, dass alle SubScriptions, die von Actions definiert wurden, wieder entfernt werden.
    }

    GetComponent(uiComponent: UiComponent): any {
        if (uiComponent && uiComponent.type && uiComponent.typeName) {
            switch (uiComponent.type.toLowerCase()) {
                case 'control':
                    return this.GetControl(uiComponent);
                case 'action':
                    // do nothing
                    return null;
                default:
                    console.warn('unknown UiComponent Type : ', uiComponent);
                    break;
            }
        }
    }

    GetControl(uiComponent: UiComponent): any {
        switch (uiComponent.typeName.toLowerCase()) {
            case 'label':
                return { component: LabelComponent };
            case 'labelwithtitle':
                return { component: LabelWithTitleComponent };
            case 'row':
                return { component: RowComponent };
            case 'column':
                return { component: ColumnComponent };
            case 'tab':
                return { component: TabComponent };
            case 'link':
                return { component: LinkComponent };
            case 'form':
                return { component: FormComponent };
            case 'submitbutton':
                return { component: SubmitButtonComponent };
            case 'image':
                return { component: ImageComponent };
            case 'target':
                return { component: TargetComponent };
            case 'input':
                return { component: InputComponent };
            case 'fileselector':
                return { component: FileSelectorComponent };
            case 'checkbox':
                return { component: CheckboxComponent };
            case 'dropdown':
                return { component: DropDownComponent };
            case 'table':
                return { component: TableComponent };
            case 'tree':
                return { component: TreeComponent };
            case 'button':
                return { component: ButtonComponent };
            case 'card':
                return { component: CardComponent };
            case 'grid':
                return { component: GridComponent };
            case 'griditem':
                return { component: GridItemComponent };
            case 'title':
                return { component: TitleComponent };
            case 'expansionpanel':
                return { component: ExpansionPanelComponent };
            case 'accordion':
                return { component: AccordionComponent };
            case 'divider':
                return { component: DividerComponent };
            case 'pdfviewer':
                return { component: PdfViewerComponent };
            case 'uidefinition':
                return { component: UiDefinitionComponent };
            default:
                console.warn('unknown UiComponent TypeName : ', uiComponent);
                return null;
        }
    }

    ExecuteActionFromName(
        actionName: string,
        storeDataChildNames: string[]
    ): Observable<UiAction> {
        return this.dynamicFacade.GetAction(actionName, storeDataChildNames).pipe(
            first(),
            mergeMap((action) => {
                return this.ExecuteAction(action, storeDataChildNames);
            })
        );
    }

    private ExecuteAction(
        uiAction: UiAction,
        storeDataChildNames: string[]
    ): Observable<UiAction> {
        uiAction.startAnotherAction = false;
        switch (uiAction?.typeName?.toLowerCase()) {
            case 'executestoredprocedure': {
                this.dynamicFacade.ExecuteStoredProcedure(uiAction, storeDataChildNames);
                uiAction.startAnotherAction = true;
                return of(uiAction);
            }
            case 'copydata': {
                this.CopyData(uiAction, storeDataChildNames);
                return of(uiAction);
            }
            case 'staticvaluetodata': {
                this.StaticValueToData(uiAction, storeDataChildNames);
                return of(uiAction);
            }
            case 'navigatetourl': {
                this.NavigateToUrl(uiAction, storeDataChildNames);
                return of(uiAction);
            }
            case 'removedata': {
                this.RemoveData(uiAction, storeDataChildNames);
                return of(uiAction);
            }
            case 'generatepdf': {
                this.dynamicFacade.GeneratePDF(uiAction, storeDataChildNames);
                uiAction.startAnotherAction = true;
                return of(uiAction);
            }
            case 'sendmail': {
                this.dynamicFacade.SendMail(uiAction, storeDataChildNames);
                uiAction.startAnotherAction = true;
                return of(uiAction);
            }
            case 'exportcsv': {
                this.dynamicFacade.ExportCsv(uiAction, storeDataChildNames);
                uiAction.startAnotherAction = true;
                return of(uiAction);
            }
            default:
                console.warn('unknown action type: ', uiAction);
                debugger;
                return of(uiAction);
        }
    }

    private CopyData(
        uiAction: UiAction,
        storeDataChildNames: string[]
    ) {
        if (uiAction.config) {
            if (
                (uiAction.config.sourceStoreData) &&
                uiAction.config.targetStoreData
            ) {
                var addToTarget = uiAction.config.addToTarget ?? false;
                var addToTargetIfNotExists =
                    uiAction.config.addToTargetIfNotExists ?? false;
                var withOperation =
                    uiAction.config.withOperation ?? false;
                // Check of the type of sourceStoreData is an array and if you add the operation
                var valueStore = this.GetValue(uiAction.config.sourceStoreData, storeDataChildNames);
                if (uiAction.config.inputParameters) {
                    if (Array.isArray(valueStore)) {
                        var newValueStore = [];
                        for (const valueStoreElement of valueStore) {
                            var newValueStoreElement: any = {};
                            for (const element of uiAction.config.inputParameters) {
                                let parameterValue = element.staticValue
                                    ? element.staticValue
                                    : valueStoreElement[element.sourceStoreDataName];
                                newValueStoreElement[element.parameterName] = parameterValue;
                            }
                            newValueStore.push(newValueStoreElement);
                        }
                        valueStore = newValueStore;
                    } else {
                        var newValueStoreElement: any = {};
                        for (const element of uiAction.config.inputParameters) {
                            let parameterValue = element.staticValue
                                ? element.staticValue
                                : valueStore[element.sourceStoreDataName];
                            newValueStoreElement[element.parameterName] = parameterValue;
                        }
                        valueStore = newValueStoreElement;
                    }
                }

                if (withOperation && Array.isArray(valueStore)) {
                    valueStore = this.AddOperation(valueStore, withOperation);
                }

                if (addToTargetIfNotExists == true) {
                    if (withOperation) {
                        this.AddValueIfNotExistsIgnoreOperation(
                            uiAction.config.targetStoreData,
                            valueStore,
                            storeDataChildNames,
                            uiAction.name
                        );
                    } else
                        this.AddValueIfNotExists(
                            uiAction.config.targetStoreData,
                            valueStore,
                            storeDataChildNames,
                            uiAction.name
                        );
                } else if (addToTarget == true) {
                    this.AddValue(
                        uiAction.config.targetStoreData,
                        valueStore,
                        storeDataChildNames,
                        uiAction.name
                    );
                } else {
                    this.SetValue(
                        uiAction.config.targetStoreData,
                        storeDataChildNames,
                        valueStore,
                        uiAction.name
                    );
                }
            }
        }
    }

    private RemoveData(uiAction: UiAction, storeDataChildNames: string[]) {
        if (uiAction.config) {
            if (uiAction.config.storeData) {
                this.RemoveValue(uiAction.config.storeData, storeDataChildNames, uiAction.name);
            }
        }
    }

    private StaticValueToData(uiAction: UiAction, storeDataChildNames: string[]) {
        if (uiAction.config) {
            if (
                uiAction.config.staticValue &&
                uiAction.config.targetStoreData
            ) {
                const delayInMilliseconds = this.GetDelayInMilliseconds(
                    uiAction.config.delayInSeconds
                );

                let valueToSet = uiAction.config.staticValue;

                if (typeof valueToSet === 'string') {
                    const trimmedValue = valueToSet.trim();
                    if (trimmedValue.startsWith('{') || trimmedValue.startsWith('[')) {
                        try {
                            valueToSet = JSON.parse(trimmedValue);
                        } catch (error) {
                            console.error('Invalid JSON string:', uiAction.config.staticValue, error);
                        }
                    }
                }

                if (Array.isArray(valueToSet) && delayInMilliseconds > 0) {
                    this.SetValue(
                        uiAction.config.targetStoreData,
                        storeDataChildNames,
                        [],
                        uiAction.name
                    );

                    valueToSet.forEach((entry, index) => {
                        window.setTimeout(() => {
                            this.AddValue(
                                uiAction.config.targetStoreData,
                                [entry],
                                storeDataChildNames,
                                uiAction.name
                            );
                        }, delayInMilliseconds * (index + 1));
                    });
                } else if (delayInMilliseconds > 0) {
                    window.setTimeout(() => {
                        this.SetValue(
                            uiAction.config.targetStoreData,
                            storeDataChildNames,
                            valueToSet,
                            uiAction.name
                        );
                    }, delayInMilliseconds);
                } else {
                    this.SetValue(
                        uiAction.config.targetStoreData,
                        storeDataChildNames,
                        valueToSet,
                        uiAction.name
                    );
                }
            }
        }
    }

    private GetDelayInMilliseconds(delayInSeconds: any): number {
        if (delayInSeconds === undefined || delayInSeconds === null || delayInSeconds === '') {
            return 0;
        }

        const parsedDelay = Number(delayInSeconds);
        if (Number.isNaN(parsedDelay) || parsedDelay < 0) {
            console.warn('Invalid delayInSeconds value. Delay is ignored:', delayInSeconds);
            return 0;
        }

        return parsedDelay * 1000;
    }

    private NavigateToUrl(
        uiAction: UiAction,
        storeDataChildNames: string[]
    ) {
        if (uiAction.config) {
            var url = uiAction.config.url;
            var inNewWindow = uiAction.config.inNewWindow ?? false;
            if (inNewWindow) {
                if (
                    url.toLowerCase().startsWith('http://') ||
                    url.toLowerCase().startsWith('https://')
                ) {
                    const completeUrl = new URL(url);
                    if (uiAction.config.inputParameters) {
                        for (const element of uiAction.config.inputParameters) {
                            if (element.staticValue) {
                                completeUrl.searchParams.append(
                                    element.parameterName,
                                    element.staticValue
                                );
                            } else {
                                completeUrl.searchParams.append(
                                    element.parameterName,
                                    this.GetValue(
                                        element.sourceStoreData,
                                        storeDataChildNames
                                    )
                                );
                            }
                        }
                    }
                    window.open(completeUrl.toString(), '_blank');
                } else {
                    var urlTree = this.router.createUrlTree([url]);
                    if (uiAction.config.inputParameters) {
                        let params: { [key: string]: string } = {};
                        for (const element of uiAction.config.inputParameters) {
                            if (element.staticValue) {
                                params[element.parameterName] =
                                    element.staticValue;
                            } else {
                                params[element.parameterName] = this.GetValue(
                                    element.sourceStoreData,
                                    storeDataChildNames
                                );
                            }
                        }
                        urlTree = this.router.createUrlTree([url], {
                            queryParams: params,
                        });
                    }
                    const completeUrl = this.router.serializeUrl(urlTree);
                    window.open(completeUrl, '_blank');
                }
            } else {
                var urlTree = this.router.createUrlTree([url]);
                if (uiAction.config.inputParameters) {
                    let params: { [key: string]: string } = {};
                    for (const element of uiAction.config.inputParameters) {
                        if (element.staticValue) {
                            params[element.parameterName] = element.staticValue;
                        } else {
                            params[element.parameterName] = this.GetValue(
                                element.sourceStoreData,
                                storeDataChildNames
                            );
                        }
                    }
                    this.router.navigate([url], { queryParams: params });
                } else {
                    this.router.navigate([url]);
                }
            }
        }
    }

    ActionExecuteStoredProcedure(
        uiAction: UiAction,
        storeDataChildNames: string[]
    ): Observable<ExecuteSpResponse> {
        if (uiAction.config) {
            if (uiAction.config.storedProcedureName) {
                return this.ExecuteSp(
                    uiAction.config.storedProcedureName,
                    uiAction.config.inputParameters,
                    uiAction.config.withoutPermission ?? false,
                    storeDataChildNames
                );
            } else return of(null);
        } else return of(null);
    }

    ActionGeneratePDF(
        uiAction: UiAction,
        storeDataChildNames: string[]
    ): Observable<GeneratePDFResponse> {
        if (uiAction.config) {
            if (uiAction.config.templateName) {
                return this.GeneratePDF(
                    this.GetValue(uiAction.config.templateName, storeDataChildNames)?.toString() ?? "",
                    Array.isArray(this.GetValue(uiAction.config.parameter1, storeDataChildNames))
                        ? JSON.stringify(this.GetValue(uiAction.config.parameter1, storeDataChildNames))
                        : this.GetValue(uiAction.config.parameter1, storeDataChildNames)?.toString() ?? "",
                    Array.isArray(this.GetValue(uiAction.config.parameter2, storeDataChildNames))
                        ? JSON.stringify(this.GetValue(uiAction.config.parameter2, storeDataChildNames))
                        : this.GetValue(uiAction.config.parameter2, storeDataChildNames)?.toString() ?? "",
                    Array.isArray(this.GetValue(uiAction.config.parameter3, storeDataChildNames))
                        ? JSON.stringify(this.GetValue(uiAction.config.parameter3, storeDataChildNames))
                        : this.GetValue(uiAction.config.parameter3, storeDataChildNames)?.toString() ?? "",
                    Array.isArray(this.GetValue(uiAction.config.parameter4, storeDataChildNames))
                        ? JSON.stringify(this.GetValue(uiAction.config.parameter4, storeDataChildNames))
                        : this.GetValue(uiAction.config.parameter4, storeDataChildNames)?.toString() ?? "",
                    Array.isArray(this.GetValue(uiAction.config.parameter5, storeDataChildNames))
                        ? JSON.stringify(this.GetValue(uiAction.config.parameter5, storeDataChildNames))
                        : this.GetValue(uiAction.config.parameter5, storeDataChildNames)?.toString() ?? ""
                );
            } else return of(null);
        } else return of(null);
    }

    ActionSendMail(
        uiAction: UiAction,
        storeDataChildNames: string[]
    ): Observable<SendMailResponse> {
        if (uiAction.config) {
            if (uiAction.config.recipients) {
                return this.SendMail(
                    this.GetValue(uiAction.config.sender, storeDataChildNames)?.toString() ?? "",
                    this.GetValue(uiAction.config.recipients, storeDataChildNames)?.toString() ?? "",
                    this.GetValue(uiAction.config.subject, storeDataChildNames)?.toString() ?? "",
                    this.GetValue(uiAction.config.mailbody, storeDataChildNames)?.toString() ?? "",
                    this.GetValue(uiAction.config.viaSMTP, storeDataChildNames)?.toString() ?? "0"
                );
            } else return of(null);
        } else return of(null);
    }

    ActionExportCsv(
        uiAction: UiAction,
        storeDataChildNames: string[]
    ): Observable<ExportCsvResponse> {
        if (uiAction.config) {
            var dataStore = this.GetValue(uiAction.config.storeData, storeDataChildNames);
            return this.ExportCsv(
                uiAction.config.withHeader ?? false,
                Array.isArray(dataStore)
                    ? JSON.stringify(dataStore)
                    : dataStore?.toString() ?? ""
                ,
                uiAction.config.withQuotes ?? false,
                uiAction.config.delimiter ?? "",
                uiAction.config.encoding ?? ""
            );
        } else return of(null);
    }

    private ExecuteSp(
        spName: string,
        parameters: SpInputParameter2[],
        withoutPermission: boolean,
        storeDataChildNames: string[]
    ): Observable<ExecuteSpResponse> {
        const headers = { 'content-type': 'application/json' };
        const body = new ExecuteSpRequest(spName, withoutPermission);
        if (!withoutPermission) {
            body.inputParameters.push(
                new SpRequestBodyInputParameter(
                    'currentPersonId',
                    '',
                    AppConstants.specialValueCurrentPersonId
                )
            );
            body.inputParameters.push(
                new SpRequestBodyInputParameter(
                    'currentVereinId',
                    '',
                    this.GetValue(AppConstants.dataStoreCurrentVereinId, storeDataChildNames) ?? 0
                )
            );
            body.inputParameters.push(
                new SpRequestBodyInputParameter(
                    'currentAnlassId',
                    '',
                    this.GetValue(AppConstants.dataStoreCurrentAnlassId, storeDataChildNames) ?? 0
                )
            );
        }
        for (const element of parameters) {
            body.inputParameters.push(
                new SpRequestBodyInputParameter(
                    element.parameterName,
                    element.sqlDataType,
                    this.GetValueFromSqlParameter(element, storeDataChildNames) ?? ''
                )
            );
        }
        return this.http.post<ExecuteSpResponse>(
            this.serviceApiUrl + '/executesp',
            body,
            { headers: headers }
        );
    }

    private GeneratePDF(
        templateName: string,
        parameter1: string = "",
        parameter2: string = "",
        parameter3: string = "",
        parameter4: string = "",
        parameter5: string = ""
    ): Observable<GeneratePDFResponse> {
        const headers = { 'content-type': 'application/json' };
        const body = new GeneratePDFRequest(templateName, parameter1, parameter2, parameter3, parameter4, parameter5);
        return this.http.post<GeneratePDFResponse>(
            this.serviceApiUrl + '/generatepdf',
            body,
            { headers: headers }
        );
    }

    private SendMail(
        sender: string,
        recipients: string[],
        subject: string,
        mailbody: string,
        viaSMTP: string
    ): Observable<SendMailResponse> {
        const headers = { 'content-type': 'application/json' };
        const body = new SendMailRequest(sender, recipients, subject, mailbody, viaSMTP);
        return this.http.post<SendMailResponse>(
            this.serviceApiUrl + '/sendmail',
            body,
            { headers: headers }
        );
    }

    private ExportCsv(
        withHeader: boolean,
        storeData: string,
        withQuotes: boolean,
        delimiter: string,
        encoding: string | null
    ): Observable<ExportCsvResponse> {
        const headers = { 'content-type': 'application/json' };
        const body = new ExportCsvRequest(withHeader, storeData, withQuotes, delimiter ?? "", encoding ?? "");
        return this.http.post<ExportCsvResponse>(
            this.serviceApiUrl + '/exportcsv',
            body,
            { headers: headers }
        );
    }

    private GetValueFromSqlParameter(parameter: SpInputParameter2, storeDataChildNames: string[]): string {
        if (parameter.staticValue) {
            return parameter.staticValue as string;
        } else {
            let [store, storeDataName, storeDataPath] =
                ConvertStoreDataToElements(parameter.sourceStoreData);
            if (store.toLowerCase() === 'specialvalue')
                return parameter.sourceStoreData;
            let value: string;
            this.dynamicFacade
                .GetData(store, storeDataName, storeDataPath, storeDataChildNames)
                .pipe(first())
                .subscribe((c) => (value = c));
            return value;
        }
    }

    GetValue(sourceStoreDataName: string, storeDataChildNames: string[]): any {
        let value: string = "";
        if (sourceStoreDataName) {
            this.GetValueAsObservable(sourceStoreDataName, storeDataChildNames)
                .pipe(first())
                .subscribe((c) => (value = c));
        }
        return value;
    }

    GetValueAsObservable(sourceStoreDataName: string, storeDataChildNames: string[], always: boolean = false): Observable<any> {
        let [store, storeDataName, storeDataPath] =
            ConvertStoreDataToElements(sourceStoreDataName);
        //console.log('GetValueAsObservable', sourceStoreDataName, store, storeDataName, storeDataPath, storeDataChildNames);
        var value = always
            ? this.dynamicFacade.dataAlways$(store, storeDataName, storeDataPath, storeDataChildNames)
            : this.dynamicFacade.data$(store, storeDataName, storeDataPath, storeDataChildNames);
        //value.pipe(first()).subscribe(c => console.log('Value for', sourceStoreDataName, 'is', c));
        return value;
    }

    SetValue(targetStoreDataName: string, storeDataChildNames: string[], value: any, actionName: string = "") {
        let [store, storeDataName, storeDataPath] =
            ConvertStoreDataToElements(targetStoreDataName);
        this.dynamicFacade.SetData(store, storeDataName, storeDataPath, storeDataChildNames, value, actionName);
    }

    SetValueInArrayWithId(targetStoreDataName: string, key: string, keyValue: any, changeKey: string, changeValue: any, storeDataChildNames: string[], actionName: string = "") {
        let [store, storeDataName, storeDataPath] =
            ConvertStoreDataToElements(targetStoreDataName);
        this.dynamicFacade.SetDataInArrayWithId(store, storeDataName, storeDataPath, key, keyValue, changeKey, changeValue, storeDataChildNames, actionName);
    }

    RemoveValue(targetStoreDataName: string, storeDataChildNames: string[], actionName: string = "") {
        let [store, storeDataName, storeDataPath] =
            ConvertStoreDataToElements(targetStoreDataName);
        this.dynamicFacade.RemoveData(store, storeDataName, storeDataPath, storeDataChildNames, actionName);
    }

    AddValue(targetStoreDataName: string, value: any[], storeDataChildNames: string[], actionName: string = "") {
        var oldValue = (this.GetValue(targetStoreDataName, storeDataChildNames) as any[]) ?? [];
        var newValue = [...oldValue, ...value];
        this.SetValue(
            targetStoreDataName,
            storeDataChildNames,
            newValue,
            actionName);
    }

    AddValueIfNotExists(
        targetStoreDataName: string,
        value: any[],
        storeDataChildNames: string[],
        actionName: string = ""
    ) {
        const merge = (
            a: any[],
            b: any[],
            predicate = (a: any[], b: any[]) => isEqual(a, b)
        ) => {
            const c = [...a]; // copy to avoid side effects
            // add all items from B to copy C if they're not already present
            b.forEach((bItem) =>
                c.some((cItem) => predicate(bItem, cItem))
                    ? null
                    : c.unshift(bItem)
            );
            return c;
        };

        var oldValue = (this.GetValue(targetStoreDataName, storeDataChildNames) as any[]) ?? [];
        var newValue = merge(oldValue, value);
        this.SetValue(
            targetStoreDataName,
            storeDataChildNames,
            newValue,
            actionName
        );
    }

    AddValueIfNotExistsIgnoreOperation(
        targetStoreDataName: string,
        value: any[],
        storeDataChildNames: string[],
        actionName: string = ""
    ) {
        const stripOperation = (obj: any) => {
            if (obj && typeof obj === 'object') {
                const { operation, ...rest } = obj;
                return rest;
            }
            return obj;
        };

        const merge = (a: any[], b: any[]) => {
            const c = [...a];
            b.forEach((bItem) => {
                const bStripped = stripOperation(bItem);
                const exists = c.some((cItem) => {
                    const cStripped = stripOperation(cItem);
                    return isEqual(bStripped, cStripped);
                });
                if (!exists) {
                    c.unshift(bItem);
                }
            });
            return c;
        };

        const oldValue = (this.GetValue(targetStoreDataName, storeDataChildNames) as any[]) ?? [];
        const newValue = merge(oldValue, value);
        this.SetValue(
            targetStoreDataName,
            storeDataChildNames,
            newValue,
            actionName
        );
    }

    private AddOperation(inputArray: any[], withOperation: boolean): any[] {
        if (!Array.isArray(inputArray)) {
            console.error('AddOperation: inputArray is not an array', inputArray);
            return [];
        }
        return inputArray.map((item) => {
            if (withOperation && item && typeof item === 'object') {
                return { ...item, operation: 'add' };
            }
            return item;
        });
    }

    public ClearFormData(formGroupName: string) {
        delete this.formControls[formGroupName];
    }


    public AddControlToFormControls(formGroupName: string, control: ElementRef) {
        if (!this.formControls[formGroupName]) {
            this.formControls[formGroupName] = [];
        }
        this.formControls[formGroupName].push(control);
    }

    public GetFormControls(formGroupName: string): ElementRef[] {
        if (!this.formControls[formGroupName]) {
            this.formControls[formGroupName] = [];
        }
        return this.formControls[formGroupName];
    }

    public IsFormValid(formGroupName: string): boolean {
        let controls = this.GetFormControls(formGroupName);
        return controls.every((control) => control?.nativeElement?.validity.valid);
    }

    GetUiDefinitionFromUrl(url: string, currentPersonId: string, currentVereinId: string, currentAnlassId: string): Observable<GetUiDefinitionResponse> {
        const headers = { 'content-type': 'application/json' };
        const body = new GetUiDefinitionRequest(
            url,
            '',
            AppConstants.specialValueCurrentPersonId,
            this.GetValue(AppConstants.dataStoreCurrentVereinId, [])?.toString() ?? '',
            this.GetValue(AppConstants.dataStoreCurrentAnlassId, [])?.toString() ?? ''
        );
        return this.http.post<GetUiDefinitionResponse>(
            this.serviceApiUrl + '/getuidefinition',
            body,
            { headers: headers }
        );
    }

    GetUiDefinitionFromName(name: string, currentPersonId: string, currentVereinId: string, currentAnlassId: string): Observable<GetUiDefinitionResponse> {
        if (this.uiDefinitionCache[name]) {
            return this.uiDefinitionCache[name];
        }
        const headers = { 'content-type': 'application/json' };
        const body = new GetUiDefinitionRequest(
            '',
            name,
            AppConstants.specialValueCurrentPersonId,
            this.GetValue(AppConstants.dataStoreCurrentVereinId, [])?.toString() ?? '',
            this.GetValue(AppConstants.dataStoreCurrentAnlassId, [])?.toString() ?? ''
        );
        var result = this.http.post<GetUiDefinitionResponse>(
            this.serviceApiUrl + '/getuidefinitionfromname',
            body,
            { headers: headers }
        ).pipe(
            map(response => response as GetUiDefinitionResponse),
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );
        this.uiDefinitionCache[name] = result; // Set to null to indicate that the request is in progress
        return result;
    }

    GetPresentation(id: string): Observable<Presentation> {
        const headers = { 'content-type': 'application/json' };
        const body = { id: id };
        return this.http.post<Presentation>(
            this.serviceApiUrl + '/getpresentation',
            body,
            { headers: headers }
        );
    }

    ResetCache() {
        this.uiDefinitionCache = {};
    }

}
